import { ArrowDownward, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, Typography } from "@mui/material";
import { type FC, type ReactNode, useId } from "react";
import { FONT_SIZE, ICON_SIZE, INK, MOTION, NEUTRAL } from "../../../theme/tokens";

/** `Inline` is a caption-sized disclosure that sits inside content (a chat answer's "what I did" line). */
export enum CollapsibleSectionVariant {
	Section = "section",
	Inline = "inline",
}

export type CollapsibleSectionProps = {
	/** Already-translated section title — the disclosure's accessible name. */
	title: string;
	open: boolean;
	onToggle: () => void;
	children: ReactNode;
	// DEFAULT: `Section`, the full-width settings header every existing caller renders.
	variant?: CollapsibleSectionVariant;
	/** Decorative glyph before the title (`Inline` only). */
	// DEFAULT: absent = the title alone.
	icon?: ReactNode;
	dataTestId?: string;
};

const HEADER_SX = {
	[CollapsibleSectionVariant.Section]: { justifyContent: "space-between", width: "100%", p: 0.5, borderRadius: 1, textAlign: "left" },
	[CollapsibleSectionVariant.Inline]: { gap: 0.5, alignSelf: "flex-start", px: 0.5, py: 0.25, mx: -0.5, borderRadius: 1, textAlign: "start" },
} as const;

const chevronSx = (open: boolean) => ({
	color: INK.MUTED,
	flexShrink: 0,
	transform: open ? "rotate(180deg)" : "rotate(0deg)",
	transition: `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

/**
 * A titled disclosure: a header row that expands and collapses the block under it.
 *
 * For stacking a long settings surface into named groups (the printout dialog's two panels) where each
 * group is arbitrary content rather than a fixed control type — `FacetFilterSection` is the checkbox-list
 * flavour of the same idea, and this is the content-agnostic one.
 *
 * The header is a native `<button>` so it is reachable and operable from the keyboard, and carries
 * `aria-expanded`/`aria-controls` so assistive technology can report the state and find the region. The
 * chevron is decorative only — it must NOT be a nested `<button>`, which is invalid inside one.
 */
export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
	title,
	open,
	onToggle,
	children,
	variant = CollapsibleSectionVariant.Section,
	icon,
	dataTestId,
}) => {
	const regionId = useId();
	const isInline = variant === CollapsibleSectionVariant.Inline;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			<Box
				component="button"
				type="button"
				onClick={onToggle}
				aria-expanded={open}
				aria-controls={regionId}
				data-testid={dataTestId}
				sx={{
					display: "flex",
					alignItems: "center",
					...HEADER_SX[variant],
					border: "none",
					background: "none",
					font: "inherit",
					cursor: "pointer",
					transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
					"&:hover": { backgroundColor: NEUTRAL[100] },
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				}}
			>
				{isInline ? (
					<>
						{icon !== undefined && (
							<Box component="span" aria-hidden sx={{ display: "inline-flex", "& > svg": { fontSize: ICON_SIZE.MICRO } }}>
								{icon}
							</Box>
						)}
						<Typography component="span" sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, fontWeight: 500 }}>
							{title}
						</Typography>
						<ExpandMore aria-hidden sx={[chevronSx(open), { fontSize: ICON_SIZE.MICRO }]} />
					</>
				) : (
					<>
						<Typography variant="body2" sx={{ color: "primary.dark", fontWeight: 600 }}>
							{title}
						</Typography>
						<ArrowDownward aria-hidden sx={[chevronSx(open), { fontSize: { xs: 16, xl: 20 } }]} />
					</>
				)}
			</Box>
			<Collapse in={open} id={regionId}>
				{children}
			</Collapse>
		</Box>
	);
};
